import {
  Box,
  Button,
  ButtonProps,
  Center,
  Flex,
  FlexProps,
  Grid,
  Image,
  SystemStyleObject,
} from '@chakra-ui/react';
import {
  DevHelpers,
  formatAddress,
  formatAddressLongMenu,
  formatEnsMenu,
  useChainEnhanced,
  useEnsData,
} from '@cyberkongz/ui-blockchain';
import { useAccount, useDisconnect } from 'wagmi';

import { useDropdownWithHover, useIsClient, useWeb3Context } from '../../hooks';
import { APP_ROUTES_ABSOLUTE } from '../../routes';
import { pxToRem } from '../../theme';
import { DropdownBase, DropdownItem } from '../Dropdown';
import { ChevronDownIcon, EthereumIconColor, PolygonIconColor, RoninIconColor } from '../icons';

function ENSAvatarContainer({
  size = 12,
  ensAvatar,
}: {
  size?: 8 | 12;
  ensAvatar?: string | null;
}) {
  return (
    <Box h={size} w={size} overflow="hidden" borderRadius="50%">
      {ensAvatar ? (
        <Image
          // sometimes users are getting blocked by gateway.ipfs.io
          src={ensAvatar.replace('gateway.ipfs.io', 'cloudflare-ipfs.com')}
          alt="Ens Avatar"
          onError={(e) => {
            e.currentTarget.style.visibility = 'none';
          }}
        />
      ) : (
        <Center w="100%" h="100%" bg="background">
          <Image
            src={
              'data:image/png;base64, iVBORw0KGgoAAAANSUhEUgAAACAAAAAbCAYAAAAdx42aAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAYwSURBVHgBxVcJUBNnFH67mwRy2AQhXNEIGJRDUpCMgGdUsGpRQQiDdRyv0XasTOtR7zbSqY4H9sBqR63VsVqtZ4taR2OpdxUVRAWrBgETIUAgARKT7G7274apU9uqIDj125n/n/3fe//75r3/eD/ASwIBYPA6MXsNiM8P/jrBJEf58AqAv4wy0gJedytJTgkG5jOtkKPWqjnwf0HLOs9RKnqcH1v6gxEQMoUh1HNuXPCWxT5ihFCn09JuBDw512iASHSvDZ/c81BumE45yRBZ2wIEK0gKGdTLcORYXQgUQSfBac/5yliJeJpka1rcsYn51E282++K62VlU0vg3f0zox3NCjDbAvi0Cfk9yxZjG7R1NscYI+SU6834qIowF5abS3eIgCfkSw+LxdLE+enyg+qNzQ67oElhv5Dfb8vubFParNZqO3ApmsQoGv3lEWM/9MR+lHKhYPvqSI1x7dQvsBZCEs2OPeLAI2t1Roykl9LyQgIe9ssKAn3tsZq3VfuGbGiyNgr4SkHxZO7e1Ak8UbhV2JJRBqX1wlaSE+r29ccQxjztXB2lFfWe0HvG0PzMPFuUvXrHW4d2NXL1Mtkx0cmD3y2ztxcBLE8ZIHC+MW5w3yuqvArrPYklovW2pb84+8HOJc1DlhsrC88Wbfupz7eUfKT34JBNQXLWc9UT4wDlFGFoZug7c7alrxF48x6VzCwdbxUra4pF53nb++xogixwv5DANDV4VUj7J9jPYpsw8wNfbqTAfHZyw7pfRh+pgp0A6emyluTk5MJFn9qIauZ+EmWjoG0tsylQhOfwxIsDB8xbm7pWUsfno2FkUeqspDvwAjxNAMsfA7yG6ElxpUecW3CLLYgMqDW1TAnKEY2K1oHqOtWmhGHucdpkobPAvb6nJW5EA20CGmMwyNJwfbQSZd4OzW73TUpiDqqtuJdxcTXoADpEYL8G8ELfgeF0g3Cz3liu4Am9jf0WjF3iyLCcPhGa1fy3iQbjyv3jjYJ7CY7ah/hlOAeIxMRZYmWybJ94efnpkuBgZVCTc4hlRvZ72aUAHSRQ1gDYrdvk4rt3jsZ2l0rrx8+ZM73/aP65rNAs8h8WGgCKLwwy000U4ASchuNAIUrCuyraU1baJLHjCkPjsB6ZKzbO79DZgGlZEs3hcvn1dPmK4nU3pnv2kn9Y6GlTzYNLwDCIAQY8OWaAZltPD4Q0PiLRXtkQ30MVwaDLFrHL/JjD4/JYZuxWwNDDVj/7SGNDhb5DBNgVzJ2ZHqG6myL7+F7eH8NFUeI2r4h1xeUR8PiKReRx7HA5gCIpsNlsbUTc7CcJCKDiJyfVl31+VcZRdbNIo/xp/a4bUnbaKhKcw53grGqPAOcAAJMW3KtlI33rm6CPUo5iITUMhhMIMxM+zYnOfi17BTFup8XJtXEZoYHT1A0CoTkQ+XC9hJJgg7ip0UwyZqiTdQ/20zXkBTaKahQK2wV9CuWkfvP29h7udL6YRNslch/d9+IDX4TYS+aJwGzVM/zKE4ICvc2/jgiz+PhZmEFDJ1ncUOBVeOZhEG3w9dYJmUj6/K0Pb395vD+vd7cPxn6Vq0vw6+e7M/ezVYy+dqipxlRFusl2SXQKarWaIzuwbGTcnDHF7C/ykotyPOPL9/8qS6xcuCF+6VBjRHgEYrdtJRuJkOfN81L1wNMY5mkcDKLdbccDMPWux55+VdbIR4pi1R60IOSk3+LwclXmQD+Xy1UiD5Gfqq+vF70yAnAGwE3giCap/4h2Z2QXt9Y25vH8h8wjNsUcCk+JwAzVhpTUsalFaWlpkldDwAN2m9IUDWyIQegtRE+L7sccv1M4fpHO7+KIzb7fD/s5eoKy+VrxtcjysvIT/ybRSWjxhPULlfKJ8VdwHLfqdDr5M9XYamn8oR8HJZpmbY3NVFkJgkB9+/YtYauotnR0qaZj+ISEphkuh8uxpqSkGJ6pxJYlBQgujTl6mGPdRHrFc4gJRfuuxEql0oWsdGUXCOQiX3yStVcUfupUIcdFukj0XFX2gDwBE8+m7t/pqJpXSg5wvTmaMjmw983mri0BTzFat2pZAHT0raBBROqN1AFzP1GN0Q4Ef9TVNdgpsAWumk39a3/gPMGfux6ht/vBhWYAAAAASUVORK5CYII='
            }
            style={{ width: '80%', height: 'auto ' }}
            alt="CK Logo"
          />
        </Center>
      )}
    </Box>
  );
}

export function AccountButtonNotConnected({ children, ...rest }: ButtonProps) {
  const { openConnectWalletModal } = useWeb3Context();

  return (
    <Button variant="systemMenu" size="sm" onClick={() => openConnectWalletModal()} {...rest}>
      {children || 'Connect Wallet'}
    </Button>
  );
}

export function AccountENSData({
  ensAvatar,
  ensName,
  address,
  styles,
}: {
  ensAvatar?: string | null;
  ensName?: string | null;
  address: `0x${string}`;
  styles?: {
    container?: FlexProps;
  };
}) {
  return (
    <Flex gap={4} alignItems="center" {...styles?.container}>
      <ENSAvatarContainer ensAvatar={ensAvatar} />
      <Grid>
        <Box
          css={{
            fontWeight: 'medium',
            textTransform: 'uppercase',
            maxWidth: ['14em', '18em'],
          }}
        >
          {ensName || 'NO ENS'}
        </Box>
        <Box color="textTertiary">{formatAddressLongMenu(address)}</Box>
      </Grid>
    </Flex>
  );
}

export function AccountButton({
  stylesButton,
  stylesDropdownBaseContainer,
}: {
  stylesButton?: SystemStyleObject;
  stylesDropdownBaseContainer?: SystemStyleObject;
}) {
  const { handleSwitchNetwork } = useWeb3Context();

  const isClient = useIsClient();
  const { disconnect } = useDisconnect();
  const { address } = useAccount();
  const { chain } = useChainEnhanced();
  const { handleOnMouseEnter, handleOnMouseLeave, handleOnClick, dropdownOpened } =
    useDropdownWithHover();
  const { ensAvatar, ensName } = useEnsData();

  return isClient && address ? (
    <Box
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
      onClick={handleOnClick}
      position="relative"
    >
      {process.env.NODE_ENV !== 'production' ? <DevHelpers /> : null}
      <Button variant="systemMenu" size="sm" p={0} {...stylesButton}>
        <Flex alignItems="center" height="inherit" borderColor="inherit">
          <Flex
            borderRight="1px"
            borderStyle="solid"
            alignItems="center"
            height="inherit"
            borderColor="inherit"
            px={pxToRem(9)}
            css={{
              '& svg': {
                boxSize: 6,
              },
            }}
          >
            {chain?.type === 'polygon' ? (
              <PolygonIconColor />
            ) : chain?.type === 'ronin' ? (
              <RoninIconColor />
            ) : (
              <EthereumIconColor />
            )}
          </Flex>
          <Flex px={pxToRem(9)} alignItems="center">
            <ENSAvatarContainer size={8} ensAvatar={ensAvatar} />
            <Box display={['none', 'block']} ml={2}>
              {ensName ? formatEnsMenu(ensName) : formatAddress(address)}
            </Box>
            <ChevronDownIcon
              ml={3}
              width={pxToRem(13)}
              height="auto"
              transition="transform 0.3s ease-in-out"
              transform={`rotateZ(${dropdownOpened ? 180 : 0}deg)`}
            />
          </Flex>
        </Flex>
      </Button>
      <DropdownBase
        dropdownOpened={dropdownOpened}
        stylesContainer={{
          right: 0,
          transform: 'translateY(100%)',
          ...stylesDropdownBaseContainer,
        }}
        sxCard={{
          overflow: 'hidden',
          w: '100%',
          minW: pxToRem(230),
        }}
      >
        <AccountENSData
          ensAvatar={ensAvatar}
          ensName={ensName}
          address={address}
          styles={{
            container: {
              p: 4,
              borderBottom: 'thin',
            },
          }}
        />
        <Grid py={1} borderBottom="thin">
          <DropdownItem href={APP_ROUTES_ABSOLUTE.PROFILE_KOLLECTION}>
            <Flex alignItems="center" justifyContent="space-between">
              Profile
            </Flex>
          </DropdownItem>
        </Grid>
        <Grid py={1}>
          <DropdownItem onClick={handleSwitchNetwork}>Switch Network</DropdownItem>
          <DropdownItem
            onClick={() => {
              disconnect();
            }}
          >
            Disconnect
          </DropdownItem>
        </Grid>
      </DropdownBase>
    </Box>
  ) : (
    <AccountButtonNotConnected {...stylesButton} />
  );
}
